import React, { useState, useEffect } from "react";
import { Container, Card, Form, Row, Col } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { useDocumentProcessor } from "../common/useDocumentProcessor";
import DocumentUploader from "../common/DocumentUploader";
import EmbeddingProcessor from "../common/EmbeddingProcessor";
import {
  createSalesFragments,
  createContentFragments,
} from "./VexaProductAnalyzer";

const VexaQuoteProcessor = ({ ruc }) => {
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [documentType, setDocumentType] = useState("cotizacion");
  const [category, setCategory] = useState("producto");
  const [clientName, setClientName] = useState("");
  const [quoteNumber, setQuoteNumber] = useState("");

  const {
    processing,
    setProcessing,
    progress,
    setProgress,
    statusMessage,
    setStatusMessage,
    error,
    setError,
    success,
    setSuccess,
    uploadToFirebase,
    indexFragmentsToFirestore,
    processEmbeddings,
    showNotification,
  } = useDocumentProcessor("vexa");

  // Cargar PDF.js
  useEffect(() => {
    const loadPdfJs = async () => {
      try {
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js";
        script.async = true;
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js";
          setPdfJsLoaded(true);
        };
      } catch (error) {
        setError(`Error cargando PDF.js: ${error.message}`);
      }
    };
    loadPdfJs();
  }, []);

  // Manejar selección de archivo
  const handleFileChange = (selectedFile) => {
    setFile(selectedFile);
    setTitle(selectedFile.name.replace(/\.pdf$/i, ""));
    setError(null);
    setSuccess(false);
  };

  // Extraer texto del PDF
  const extractText = async () => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({
      data: new Uint8Array(arrayBuffer),
    }).promise;

    let fullText = "";
    const fragments = [];

    const documentMetadata = {
      title: title || file.name.replace(/\.pdf$/, ""),
      description: `Cotización para cliente: ${clientName} - N° ${quoteNumber}`,
      pageCount: pdf.numPages,
      extractionDate: new Date().toISOString(),
      documentId: uuidv4().substring(0, 8),
      agent_type: "vexa",
      sourceFilename: file.name,
      ruc: ruc,
    };

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = extractPageText(textContent);
      fullText += pageText + "\n\n";

      // Fragmentación específica para vexa
      const salesFragments = createSalesFragments(pageText, i);
      fragments.push(...salesFragments);
    }

    const enhancedFragments = fragments.map((fragment, index) => ({
      ...fragment,
      id: `${documentMetadata.documentId}_pg${fragment.pageNumber}_f${index}`,
      document_id: documentMetadata.documentId,
      extractionDate: documentMetadata.extractionDate,
      agent_type: "vexa",
      page: fragment.pageNumber,
      char_count: fragment.text.length,
      structure_type: fragment.structureType || null,
      structure_header: fragment.structureHeader || null,
      agentSpecificFields: {
        product_category: fragment.product_category,
        price_range: fragment.price_range,
        product_name: fragment.product_name,
        price_value: fragment.price_value,
        section_type: fragment.section_type,
      },
    }));

    return {
      text: fullText,
      fragments: enhancedFragments,
      pageCount: pdf.numPages,
      metadata: documentMetadata,
    };
  };

  // Extraer texto de página
  const extractPageText = (textContent) => {
    let lastY = -1;
    let text = "";
    const textItems = textContent.items.sort((a, b) => {
      if (Math.abs(a.transform[5] - b.transform[5]) < 5)
        return a.transform[4] - b.transform[4];
      return b.transform[5] - a.transform[5];
    });

    for (const item of textItems) {
      const currentY = item.transform[5];
      if (lastY !== -1 && Math.abs(currentY - lastY) > 5) text += "\n";
      if (text.length > 0 && !text.endsWith(" ") && !item.str.startsWith(" "))
        text += " ";
      text += item.str;
      lastY = currentY;
    }

    return text
      .replace(/\s+\n/g, "\n")
      .replace(/\n\s+/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Proceso completo
  const handleProcessCompleteDocument = async () => {
    if (!file || !ruc || !clientName) {
      setError("Se requiere archivo, RUC y nombre del cliente");
      return;
    }

    try {
      setProcessing(true);
      setProgress(0);
      setError(null);
      setSuccess(false);

      setStatusMessage("Subiendo documento...");
      const uploadResult = await uploadToFirebase(file);
      setProgress(20);

      setStatusMessage("Analizando productos y precios...");
      const extractResult = await extractText();
      setProgress(40);

      setStatusMessage("Preparando cotización...");
      const agentSpecificFields = {
        document: {
          client_name: clientName,
          quote_number: quoteNumber,
        },
        fragment: {},
      };
      await indexFragmentsToFirestore(
        uploadResult.storagePath,
        extractResult.fragments,
        extractResult.metadata,
        agentSpecificFields
      );
      setProgress(70);

      setStatusMessage("Generando embeddings...");
      await processEmbeddings(
        extractResult.metadata.documentId,
        documentType,
        category,
        ruc
      );
      setProgress(100);

      setStatusMessage("¡Cotización procesada!");
      setSuccess(true);
      showNotification(
        "¡Listo!",
        "Cotización preparada para consultas.",
        "success"
      );
    } catch (error) {
      console.error("Error:", error);
      setError(`Error: ${error.message}`);
      showNotification("Error", error.message, "error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Header>
          <h4>💰 Vexa - Procesador de Cotizaciones</h4>
          <p>
            Especializado en análisis de productos, precios y manuales
            comerciales
          </p>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <DocumentUploader
                file={file}
                onFileChange={handleFileChange}
                processing={processing}
                agentType="vexa"
              />
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Documento</Form.Label>
                  <Form.Select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    disabled={processing}
                  >
                    <option value="cotizacion">Cotización</option>
                    <option value="manual">Manual de Producto</option>
                    <option value="catalogo">Catálogo</option>
                    <option value="propuesta">Propuesta Comercial</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Título del Documento</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={processing}
                placeholder="Ej: Cotización Software Empresarial"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del Cliente</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    disabled={processing}
                    placeholder="Ej: Empresa XYZ S.A."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Cotización</Form.Label>
                  <Form.Control
                    type="text"
                    value={quoteNumber}
                    onChange={(e) => setQuoteNumber(e.target.value)}
                    disabled={processing}
                    placeholder="Ej: COT-2024-001"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoría</Form.Label>
                  <Form.Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={processing}
                  >
                    <option value="producto">Producto</option>
                    <option value="servicio">Servicio</option>
                    <option value="precio">Precio</option>
                    <option value="comercial">Comercial</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <EmbeddingProcessor
              processing={processing}
              progress={progress}
              statusMessage={statusMessage}
              error={error}
              success={success}
              onProcess={handleProcessCompleteDocument}
              disabled={!file || !clientName}
            />
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default VexaQuoteProcessor;
